import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { updateDomainField } from "@/components/dashboard/DomainsTable/domainService";
import type { EditableCellValue } from "@/components/dashboard/EditableCell";
import type { Domain } from "@/types/domain";
import type { DashboardData } from "@/types/dashboard";

const formatDomainFieldValue = (value: EditableCellValue): EditableCellValue =>
  value === "" ? null : value;

type UpdateDomainFieldVariables = {
  id: string;
  column: string;
  value: EditableCellValue;
};

type UpdateDomainFieldContext = {
  snapshots: [readonly unknown[], DashboardData | undefined][];
};

const patchDomain = (
  domain: Domain,
  column: string,
  formattedValue: EditableCellValue,
): Domain => ({
  ...domain,
  [column]: formattedValue as Domain[keyof Domain],
});

export const useUpdateDomainField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, column, value }: UpdateDomainFieldVariables) =>
      updateDomainField(id, column, value),
    onMutate: async (variables): Promise<UpdateDomainFieldContext> => {
      const formattedValue = formatDomainFieldValue(variables.value);

      await queryClient.cancelQueries({ queryKey: ["dashboard"] });

      const snapshots = queryClient.getQueriesData<DashboardData>({
        queryKey: ["dashboard"],
      });

      queryClient.setQueriesData<DashboardData>(
        { queryKey: ["dashboard"] },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            domains: old.domains.map((domain) =>
              domain.id === variables.id
                ? patchDomain(domain, variables.column, formattedValue)
                : domain,
            ),
          };
        },
      );

      return { snapshots };
    },
    onError: (error, _variables, context) => {
      context?.snapshots.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      const message =
        error instanceof Error ? error.message : "Unknown error";
      toast.error("Error saving: " + message);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};
